import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { combineLatestWith, filter, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { StepperOrientation } from '@angular/material/stepper';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ORConnectionChangeOperationsForm,
  OrderRequestWizardModelForm
} from '@bo-client/shared/models/orderRequestWizard.model';
import { OrderRequestWizardService } from '@bo-client/nap/order-request-wizard/order-request-wizard.service';
import {
  ConnectionChangeDto,
  ConnectionChangeRequestTypeDisplayValues,
  ConnectionChangeRequestTypeEnum
} from '@bo-client/shared/models/connectionChange.dto';
import {
  AllAreaEnum,
  AllAreaTypeEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { ToastrService } from 'ngx-toastr';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectionChangeRequestTypeConfig } from '@bo-client/shared/config.model';
import { Observe } from '@bo-client/shared/observe.util';
import { ConnectionObject, ConnectionObjectFormV2 } from '@bo-client/shared/models/connectionObject';

@Component({
  selector: 'app-order-request-wizard',
  templateUrl: './order-request-wizard.component.html',
  styleUrls: ['./order-request-wizard.component.scss']
})
export class OrderRequestWizardComponent implements OnInit {
  @Input() model: ConnectionChangeDto;
  @Observe('model') model$: Observable<ConnectionChangeDto>;
  @Input() info: string | null = null;
  @Input() token: string | null = null;

  @Output() onSubmitModel = new EventEmitter<ConnectionChangeDto>();

  private breakpointObserver = inject(BreakpointObserver);
  private orderRequestWizardService = inject(OrderRequestWizardService);
  private toastrService = inject(ToastrService);
  private dataShareService = inject(DataShareService);
  private destroyRef = inject(DestroyRef);

  form$: Observable<OrderRequestWizardModelForm>;

  stepperOrientation$: Observable<StepperOrientation>;
  summaryData$: Observable<ConnectionChangeDto>;
  operationsValue$: Observable<ORConnectionChangeOperationsForm['value']>;
  _availableAreas$ = new BehaviorSubject<{ areas: AllAreaEnum[]; touched: boolean }>({
    areas: [],
    touched: false
  });
  availableAreas$: Observable<{ areas: AllAreaEnum[]; touched: boolean }> =
    this._availableAreas$.asObservable();
  config$: Observable<ConnectionChangeRequestTypeConfig>;
  introConfig$: Observable<string>;

  ngOnInit() {
    this.config$ = this.dataShareService
      .getSingleTenantConfig$<ConnectionChangeRequestTypeConfig[]>(
        'connectionChangeRequest.connectionChangeRequestTypes'
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        combineLatestWith(this.model$),
        map(
          ([config, model]) =>
            ConnectionChangeRequestTypeConfig.getConnectionChangeRequestTypeConfig(
              model.area,
              model.connectionChangeRequestType,
              config
            ) || {}
        ),
        shareReplay(1)
      );
    this.introConfig$ = this.config$.pipe(map((config) => config.introText));
    this.stepperOrientation$ = this.breakpointObserver
      .observe('(min-width: 1400px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
    this.initForm();
  }

  submitOrder(form: OrderRequestWizardModelForm) {
    console.log('onSubmit', form, form.value);
    if (form.invalid) {
      this.toastrService.error(
        'Bitte beachten Sie die rot markierten Felder und befolgen Sie die Hinweistexte.',
        'Formular ungültig'
      );
      form.markAllAsTouched();
      return;
    }
    this.onSubmitModel.emit(this.combineModelAndFormData(form.value));
  }

  private combineModelAndFormData(formValue: OrderRequestWizardModelForm['value']): ConnectionChangeDto {
    const {
      connectionObject,
      connectionChangeOperations,
      contactData,
      technicalData: _technicalData,
      additionalInformation: _additionalInformation
    } = formValue;
    const { sitePlanUploads, suspectedOrdnanceAreaUploads, ...technicalData } = _technicalData;
    const {
      mandate: mandateUploads,
      ownership: ownershipUploads,
      ...additionalInformation
    } = _additionalInformation;
    return {
      ...this.model,
      connectionObject,
      ...connectionChangeOperations,
      ...technicalData,
      ...additionalInformation,
      customer: contactData.customer,
      invoiceContact: contactData.invoiceContact,
      uploads: [...sitePlanUploads, ...suspectedOrdnanceAreaUploads, ...mandateUploads, ...ownershipUploads]
    } as ConnectionChangeDto;
  }

  protected readonly AllAreaTypeEnum = AllAreaTypeEnum;
  protected readonly ConnectionChangeRequestTypeDisplayValues = ConnectionChangeRequestTypeDisplayValues;
  protected readonly ConnectionChangeRequestTypeEnum = ConnectionChangeRequestTypeEnum;

  private initForm() {
    this.form$ = this.orderRequestWizardService.createStandardForm$(this.model).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((form) => {
        const orgUnitControl = form.controls.connectionObject.controls.orgUnitDesc;
        return orgUnitControl.valueChanges
          .pipe(
            startWith(orgUnitControl.value),
            combineLatestWith(this.dataShareService.companyLocations$),
            filter(([orgUnitDesc, companyLocations]) => !!orgUnitDesc && !!companyLocations),
            map(([orgUnitDesc, companyLocations]) => {
              return companyLocations.find((x) => x.external_id === orgUnitDesc);
            })
          )
          .subscribe({
            next: (value) => {
              if (value) {
                this.model.companyLocation = value;
              }
            }
          });
      }),
      shareReplay(1)
    );
    this.operationsValue$ = this.form$.pipe(
      switchMap((form) =>
        form.controls.connectionChangeOperations.valueChanges.pipe(
          startWith({ ...form.controls.connectionChangeOperations.value }),
          filter(
            (value) =>
              this.model.powerIncrease !== value.powerIncrease ||
              this.model.moveConnectionLine !== value.moveConnectionLine ||
              this.model.moveServiceEntranceBox !== value.moveServiceEntranceBox
          ),
          map((value) => {
            return {
              ...value
            };
          }),
          shareReplay(1)
        )
      )
    );
    this.summaryData$ = this.form$.pipe(
      switchMap((form) =>
        form.valueChanges.pipe(
          startWith(form.value),
          map((formValue) => {
            return this.combineModelAndFormData(formValue);
          })
        )
      )
    );
  }

  setAvailableAreas(availableAreas: AllAreaEnum[], form: ConnectionObjectFormV2, data: ConnectionObject) {
    this._availableAreas$.next({
      areas: availableAreas || [],
      touched: form.touched || data.gisId !== form.value.gisId
    });
  }

  logData(form: OrderRequestWizardModelForm) {
    console.log('Form Data:', form);
  }
}
